import Editor from '@monaco-editor/react';
import PropTypes from 'prop-types';
import React, {useEffect, useMemo, useState} from 'react';
import {Box, Button, Paper, Typography} from '@mui/material';

const EDITABLE_PATHS = [
  {
    path: 'context.product_accesses',
    label: 'Product Accesses',
    readOnly: false,
  },
  {path: 'overlay', label: 'Overlay', readOnly: false},
  {path: 'context.legacy_plans', label: 'Legacy Plans', readOnly: true},
  {
    path: 'context.salesforce_orders',
    label: 'Salesforce Orders',
    readOnly: true,
  },
  {
    path: 'context.salesforce_products',
    label: 'Salesforce Products',
    readOnly: true,
  },
  {
    path: 'context.existing_product_accesses',
    label: 'Existing Product Accesses',
    readOnly: true,
  },
];

function MigrationJsonEditor({
  migrationData,
  onSave,
  onDiscard,
  isLoading = false,
}) {
  const [selectedPath, setSelectedPath] = useState(EDITABLE_PATHS[0].path);
  const [editedData, setEditedData] = useState({});
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize edited data from migrationData
  useEffect(() => {
    if (migrationData) {
      // Extract the actual migration plan from the wrapper structure
      const actualPlan = migrationData.results?.migration_plan || migrationData;

      const initialData = {};
      EDITABLE_PATHS.forEach(({path}) => {
        const value = getNestedValue(actualPlan, path);
        initialData[path] = value;
      });
      setEditedData(initialData);
      setHasChanges(false);
    }
  }, [migrationData]);

  // Debug effect to track path changes
  useEffect(() => {
    if (migrationData) {
      const actualPlan = migrationData.results?.migration_plan || migrationData;
      const value = getNestedValue(actualPlan, selectedPath);
      // Ensure the selected path always has data in editedData
      setEditedData(prevData => {
        if (prevData[selectedPath] === undefined) {
          return {
            ...prevData,
            [selectedPath]: value,
          };
        }
        return prevData;
      });
    }
  }, [selectedPath, migrationData]);

  // Get nested value from object path
  function getNestedValue(obj, path) {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  }

  // Set nested value in object path
  function setNestedValue(obj, path, value) {
    const keys = path.split('.');
    const lastKey = keys.pop();
    const target = keys.reduce((current, key) => {
      if (!current[key]) current[key] = {};
      return current[key];
    }, obj);
    target[lastKey] = value;
  }

  const currentSelectedConfig = EDITABLE_PATHS.find(
    p => p.path === selectedPath
  );

  // Get current value - use edited data if available, otherwise get from source
  const currentValue = useMemo(() => {
    if (editedData[selectedPath] !== undefined) {
      return editedData[selectedPath];
    }
    // Fallback to source data if not in editedData yet
    if (migrationData) {
      const actualPlan = migrationData.results?.migration_plan || migrationData;
      return getNestedValue(actualPlan, selectedPath);
    }
    return {};
  }, [selectedPath, editedData, migrationData]);

  const handleEditorChange = value => {
    try {
      const parsedValue = JSON.parse(value);
      setEditedData(prevData => ({
        ...prevData,
        [selectedPath]: parsedValue,
      }));
      setHasChanges(true);
    } catch (error) {
      // Invalid JSON - don't update the state
      // Silently ignore invalid JSON until user fixes syntax
    }
  };

  const handleSave = () => {
    if (!hasChanges || isLoading) return;

    // Extract the actual migration plan from the wrapper structure
    const actualPlan = migrationData.results?.migration_plan || migrationData;

    // Create a copy of the actual migration plan data
    const updatedMigrationPlan = JSON.parse(JSON.stringify(actualPlan));

    // Apply all changes from editedData to the migration plan
    Object.entries(editedData).forEach(([path, value]) => {
      if (!EDITABLE_PATHS.find(p => p.path === path)?.readOnly) {
        setNestedValue(updatedMigrationPlan, path, value);
      }
    });

    // The save function expects just the migration plan data, not the wrapper
    onSave?.(updatedMigrationPlan);
    setHasChanges(false);
  };

  const handleDiscard = () => {
    if (migrationData) {
      // Extract the actual migration plan from the wrapper structure
      const actualPlan = migrationData.results?.migration_plan || migrationData;

      const resetData = {};
      EDITABLE_PATHS.forEach(({path}) => {
        const value = getNestedValue(actualPlan, path);
        resetData[path] = value;
      });
      setEditedData(resetData);
      setHasChanges(false);
    }
    onDiscard?.();
  };

  const jsonString = useMemo(() => {
    try {
      // Handle undefined or null values
      if (currentValue === undefined || currentValue === null) {
        return '{}';
      }
      return JSON.stringify(currentValue, null, 2);
    } catch {
      return '{}';
    }
  }, [currentValue]);

  if (!migrationData) {
    return (
      <Box sx={{p: 3, textAlign: 'center'}}>
        <Typography color="text.secondary">
          No migration data available
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{height: '600px', display: 'flex', border: '1px solid #e0e0e0'}}>
      {/* Left Panel - File Tree */}
      <Paper
        sx={{
          width: '300px',
          borderRadius: 0,
          borderRight: '1px solid #e0e0e0',
          overflow: 'auto',
        }}
        elevation={0}
      >
        <Box sx={{p: 2, borderBottom: '1px solid #e0e0e0'}}>
          <Typography variant="subtitle2" fontWeight={600}>
            Editable Properties
          </Typography>
        </Box>
        <Box>
          {EDITABLE_PATHS.map(({path, label, readOnly}) => (
            <Box
              key={path}
              onClick={() => setSelectedPath(path)}
              sx={{
                p: 2,
                cursor: 'pointer',
                backgroundColor:
                  selectedPath === path ? 'action.selected' : 'transparent',
                borderBottom: '1px solid #f0f0f0',
                '&:hover': {
                  backgroundColor:
                    selectedPath === path ? 'action.selected' : 'action.hover',
                },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: readOnly ? 'text.secondary' : 'text.primary',
                  fontWeight: selectedPath === path ? 600 : 400,
                }}
              >
                {label}
              </Typography>
              {readOnly && (
                <Typography variant="caption" color="warning.main">
                  Read-only
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      </Paper>

      {/* Right Panel - JSON Editor */}
      <Box sx={{flex: 1, display: 'flex', flexDirection: 'column'}}>
        {/* Header */}
        <Box
          sx={{
            p: 2,
            borderBottom: '1px solid #e0e0e0',
            backgroundColor: '#fafafa',
          }}
        >
          <Typography variant="subtitle2" fontWeight={600}>
            {currentSelectedConfig?.label || selectedPath}
            {currentSelectedConfig?.readOnly && (
              <Typography
                component="span"
                variant="caption"
                color="warning.main"
                sx={{ml: 1}}
              >
                (Read-only)
              </Typography>
            )}
          </Typography>
        </Box>

        {/* Editor */}
        <Box sx={{flex: 1, position: 'relative'}}>
          <Editor
            key={selectedPath}
            height="100%"
            language="json"
            value={jsonString}
            onChange={handleEditorChange}
            options={{
              readOnly: currentSelectedConfig?.readOnly,
              minimap: {enabled: false},
              scrollBeyondLastLine: false,
              automaticLayout: true,
              formatOnPaste: true,
              formatOnType: true,
              wordWrap: 'on',
              wrappingIndent: 'indent',
            }}
            theme="vs-light"
          />
        </Box>

        {/* Action Buttons */}
        {hasChanges && (
          <Box
            sx={{
              p: 2,
              borderTop: '1px solid #e0e0e0',
              backgroundColor: '#f8f9fa',
              display: 'flex',
              gap: 2,
              justifyContent: 'flex-end',
            }}
          >
            <Button
              variant="outlined"
              onClick={handleDiscard}
              disabled={isLoading}
            >
              Discard
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={isLoading}
            >
              Save
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
}

MigrationJsonEditor.propTypes = {
  migrationData: PropTypes.object,
  onSave: PropTypes.func,
  onDiscard: PropTypes.func,
  isLoading: PropTypes.bool,
};

export default MigrationJsonEditor;
